import { FileUploadingStatus, ProjectUserPermissions } from "@/enums";
import { useProjectContext } from "@/providers";
import {
  useGetCDNFilePathQuery,
  useGetSasMutation,
  useUploadFileMutation,
} from "@/services";
import {
  useGetDocumentsForMilestoneQuery,
  useRemoveProjectMilestoneDocumentMutation,
  useUploadProjectMilestoneDocumentMutation,
} from "@/services/projectMilestoneDocument";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import { faDownload, faUpload } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Spin, Upload } from "antd";
import { shape, string } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { v4 } from "uuid";
import { FullscreenModal } from "./styled";

export const MilestonePanelDocuments = ({ milestone }) => {
  const { projectId } = useParams();
  const intl = useIntl();
  const [fileList, setFileList] = useState([]);
  const [fileId, setFileId] = useState(null);
  const { data: cdnFilePath } = useGetCDNFilePathQuery(
    {
      projectId,
      fileId,
    },
    { skip: !fileId }
  );

  const {
    data: mapping,
    isFetching: isMappingFetching,
    refetch: refetchDocuments,
  } = useGetDocumentsForMilestoneQuery({
    projectId,
    milestoneId: milestone.id,
  });

  const [removeProjectMilestoneDocument] =
    useRemoveProjectMilestoneDocumentMutation();

  const [uploadProjectMilestoneDocument] =
    useUploadProjectMilestoneDocumentMutation();

  const [uploadFile] = useUploadFileMutation();

  const [getSas] = useGetSasMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState([]);

  const { canUser } = useProjectContext();
  const { Dragger } = Upload;

  const convertToUploadItem = (documentArray) =>
    documentArray?.map((document) => ({
      ...document,
      uid: document.id,
      name: document.name,
    }));

  const downloadAndCloseModal = (filePath) => {
    const link = document.createElement("a");
    link.href = filePath;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (!mapping?.[milestone.id]) return;

    const files = convertToUploadItem(mapping?.[milestone.id]);
    setFileList(files);
  }, [mapping, milestone.id]);

  const removeSpecificProjectMilestoneDocument = (file) =>
    new Promise((resolve, reject) => {
      if (!file.id) {
        resolve();
        return;
      }
      removeProjectMilestoneDocument({
        projectId,
        documentId: file.id,
      })
        .unwrap()
        .then(() => {
          refetchDocuments();
          resolve();
        })
        .catch(() => {
          toast.error(intl.formatMessage({ id: "general.error" }));
          reject();
        });
    });

  const uploadItem = ({ file, onProgress, onSuccess }) => {
    const storageInfo = { name: file.name, id: v4() };
    getSas({ storageInfo, intakeId: projectId })
      .unwrap()
      .then((response) => {
        onProgress({ percent: 5, loaded: 0, total: file.size });
        return uploadFile({
          file,
          location: response.data.itemPath,
          onProgress: ({ percent, loaded, total }) => {
            const newProgress = {
              percent: (percent / 100) * 90 + 5,
              loaded: loaded,
              total: total,
            };
            onProgress(newProgress);
          },
        })
          .unwrap()
          .then((blobInfo) => {
            const uploadRequest = {
              id: storageInfo.id,
              itemName: file.name,
              contentType: file.type,
              blobListIds: blobInfo.blockIds,
            };
            return uploadProjectMilestoneDocument({
              uploadRequest,
              projectId,
              milestoneId: milestone.id,
            })
              .unwrap()
              .then(() => {
                onSuccess("success");
                refetchDocuments();
              });
          });
      })
      .catch(() => {
        toast.error(intl.formatMessage({ id: "general.uploadFile" }));
      });
  };

  const onChange = (data) => {
    const currentFile = fileList?.find((file) => file.uid === data.file.uid);

    if (!currentFile) return;

    const filesToKeep = fileList?.filter((file) => file.uid !== data.file.uid);

    if (data.file.status === FileUploadingStatus.removed) {
      setFileList(filesToKeep);
    } else {
      currentFile.percent = data.file.percent;
      if (data.file.status) {
        currentFile.status = data.file.status;
      }
      setFileList([...filesToKeep, currentFile]);
    }
  };

  const beforeUpload = (fileToUpload) => {
    const fileAlreadyExists = fileList?.find(
      (file) => file.uid === fileToUpload.uid
    );

    if (fileAlreadyExists) return;

    setFileList([...fileList, fileToUpload]);
  };

  const setPreviewDoc = (document) => {
    setFileId(document.url.replace("projects/" + projectId + "/", ""));
    setPreviewFile([document]);
  };

  useEffect(() => {
    if (fileId && previewFile && cdnFilePath) {
      setIsModalOpen(true);
    }
  }, [fileId, previewFile, cdnFilePath]);

  if (isMappingFetching) {
    return <Spin />;
  }

  return (
    <>
      <Dragger
        data-test-id="milestone-panel-document-file-upload"
        beforeUpload={beforeUpload}
        onChange={onChange}
        listType="picture"
        customRequest={uploadItem}
        onRemove={removeSpecificProjectMilestoneDocument}
        fileList={fileList}
        onPreview={(e) => setPreviewDoc(e)}
        showUploadList={{
          showRemoveIcon:
            canUser(ProjectUserPermissions.EditMilestoneDocument, milestone) ||
            canUser(ProjectUserPermissions.EditCompletedProject),
        }}
        multiple={true}
        disabled={
          !canUser(ProjectUserPermissions.EditMilestoneDocument, milestone) &&
          !canUser(ProjectUserPermissions.EditCompletedProject)
        }
      >
        <p className="ant-upload-drag-icon">
          <FontAwesomeIcon icon={faUpload} />
        </p>
        <p className="ant-upload-text">
          Click or drag file to this area to upload
        </p>
      </Dragger>
      {cdnFilePath && (
        <FullscreenModal
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          centered
          onOk={() => downloadAndCloseModal(cdnFilePath)}
          okButtonProps={{ icon: <FontAwesomeIcon icon={faDownload} /> }}
          okText={intl.formatMessage({ id: "general.download" })}
        >
          <DocViewer
            documents={previewFile?.map((file) => ({
              uri: cdnFilePath,
              fileName: file.name,
            }))}
            pluginRenderers={DocViewerRenderers}
          />
        </FullscreenModal>
      )}
    </>
  );
};

MilestonePanelDocuments.propTypes = {
  milestone: shape({
    id: string,
    Status: string,
  }),
};
